import React, { useCallback, useEffect, useRef, useState } from "react";
import { loadFace, faceTemplate, faceBox } from "../lib/face";
import { renderToStaticMarkup } from "react-dom/server";
import api, { fmtErr } from "../lib/api";
import { printHtml } from "../lib/print";
import { useAuth } from "../context/AuthContext";
import AnnaTicketSlip, { istDateTime } from "../components/AnnaTicketSlip";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Camera, CameraOff, Printer, Keyboard, Soup, CheckCircle2, ScanFace, Ban } from "lucide-react";
import { toast } from "sonner";
import DirectPrintBanner, { directPrintOn } from "../components/DirectPrintBanner";

export const ANNA_TYPES = [
  { key: "GENERAL", label: "General", cls: "from-amber-100 to-orange-300" },
  { key: "KALYANAM", label: "Kalyanam", cls: "from-rose-100 to-pink-400" },
  { key: "VIP", label: "VIP", cls: "from-yellow-100 to-yellow-400" },
];
export const ANNA_CATS = [
  { key: "M", label: "Male", cls: "bg-sky-100 text-sky-900" },
  { key: "F", label: "Female", cls: "bg-pink-100 text-pink-900" },
  { key: "C", label: "Children", cls: "bg-amber-100 text-amber-900" },
  { key: "O", label: "Others", cls: "bg-slate-200 text-slate-800" },
];

export function AnnaStats({ stats }) {
  const boxes = [...ANNA_CATS.map((c) => [c.label, stats?.[c.key], c.cls]),
    ["Verified", stats?.verified, "bg-green-100 text-green-900"], ["Total", stats?.total, "bg-gold-50 text-maroon-900 border border-gold-400"]];
  return (
    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="anna-stats">
      {boxes.map(([label, n, cls]) => (
        <div key={label} className={`rounded-xl px-2 py-3 text-center ${cls}`}>
          <p className="text-2xl font-bold leading-none">{n ?? 0}</p>
          <p className="text-xs mt-1">{label}</p>
        </div>
      ))}
    </div>
  );
}

const onWindowsPc = /Windows/.test(navigator.userAgent);

const savedType = () => { try { return localStorage.getItem("pd_anna_type") || "GENERAL"; } catch { return "GENERAL"; } };

export default function AnnaIssue() {
  const { user } = useAuth();
  const [temples, setTemples] = useState([]);
  const [counterName, setCounterName] = useState("");
  const [templeId, setTempleId] = useState(user.temple_id || "");
  const [type, setType] = useState(savedType);
  const [photo, setPhoto] = useState("");
  const [camOn, setCamOn] = useState(false);
  const [camError, setCamError] = useState("");
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [last, setLast] = useState(null);
  const [faceDays, setFaceDays] = useState(0);     // face-recognition restriction period (0 = off)
  const [faceState, setFaceState] = useState("off"); // off | loading | ready | error
  const [blocked, setBlocked] = useState(null);      // last duplicate-token refusal shown to the operator
  const shotCanvasRef = useRef(null);
  const overlayRef = useRef(null);
  const [faceSeen, setFaceSeen] = useState(null);  // live camera: true = face found, false = none
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const busyRef = useRef(false);
  const temple = temples.find((t) => t.temple_id === templeId) || null;

  useEffect(() => {
    api.get("/meta/form-data").then((r) => {
      setTemples(r.data.temples || []);
      setCounterName((r.data.counters || []).find((c) => c.id === user.counter_id)?.name || "");
      if (!templeId && r.data.temples?.length) setTempleId(r.data.temples[0].temple_id);
    }).catch(() => {});
  }, []); // eslint-disable-line
  // Preload logos so they are ready the moment a ticket prints.
  useEffect(() => {
    [temple?.logo, temple?.logo_right].filter(Boolean).forEach((src) => { const i = new Image(); i.src = src; });
    if (temple?.name_telugu) document.fonts?.load(`19px "Dhurjati"`, temple.name_telugu).catch(() => {});
  }, [temple]);
  useEffect(() => { try { localStorage.setItem("pd_anna_type", type); } catch { /* ignore */ } }, [type]);
  // Face-recognition duplicate check: read the temple setting and load the face models when it is on.
  useEffect(() => {
    if (!templeId) return;
    api.get("/anna/settings", { params: { temple_id: templeId } }).then((r) => {
      const d = Number(r.data.face_days) || 0;
      setFaceDays(d);
      if (!d) { setFaceState("off"); return; }
      setFaceState("loading");
      loadFace().then(() => setFaceState("ready")).catch(() => setFaceState("error"));
    }).catch(() => {});
  }, [templeId]);

  const loadStats = useCallback(() => {
    if (!templeId) return;
    api.get("/anna/stats", { params: { ticket_type: type, temple_id: templeId } }).then((r) => setStats(r.data)).catch(() => {});
  }, [type, templeId]);
  useEffect(() => { loadStats(); const t = setInterval(loadStats, 30000); return () => clearInterval(t); }, [loadStats]);

  const startCamera = async () => {
    setCamError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640 } });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCamOn(true);
    } catch (e) {
      setCamError(`Camera not available: ${e?.message || e}. Allow camera access for this site.`);
      setCamOn(false);
    }
  };
  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCamOn(false);
  };
  useEffect(() => { startCamera(); return stopCamera; }, []); // eslint-disable-line

  // Live face box: while the face check is on, find the face a few times a second and draw a green box on it.
  useEffect(() => {
    if (faceState !== "ready" || !camOn) { setFaceSeen(null); return undefined; }
    let stop = false, timer;
    const tick = async () => {
      const v = videoRef.current, cv = overlayRef.current;
      if (stop || !v || !cv) return;
      try {
        if (v.videoWidth && !busyRef.current) {
          const box = await faceBox(v);
          if (stop) return;
          const W = cv.clientWidth, H = cv.clientHeight;
          cv.width = W; cv.height = H;
          const ctx = cv.getContext("2d");
          ctx.clearRect(0, 0, W, H);
          setFaceSeen(!!box);
          if (box) {
            // the video is shown with object-cover: map video pixels to the displayed area
            const scale = Math.max(W / v.videoWidth, H / v.videoHeight);
            const ox = (W - v.videoWidth * scale) / 2, oy = (H - v.videoHeight * scale) / 2;
            const x = box.x * scale + ox, y = box.y * scale + oy, w = box.width * scale, h = box.height * scale;
            const c = Math.min(w, h) * 0.22;
            ctx.strokeStyle = "#22c55e"; ctx.lineWidth = 3; ctx.shadowColor = "rgba(0,0,0,0.5)"; ctx.shadowBlur = 4;
            ctx.strokeRect(x, y, w, h);
            ctx.lineWidth = 6;
            [[x, y, 1, 1], [x + w, y, -1, 1], [x, y + h, 1, -1], [x + w, y + h, -1, -1]].forEach(([px, py, dx, dy]) => {
              ctx.beginPath(); ctx.moveTo(px, py + dy * c); ctx.lineTo(px, py); ctx.lineTo(px + dx * c, py); ctx.stroke();
            });
            ctx.shadowBlur = 0; ctx.fillStyle = "#16a34a"; ctx.fillRect(x, Math.max(0, y - 24), 86, 22);
            ctx.fillStyle = "#fff"; ctx.font = "bold 13px system-ui, sans-serif"; ctx.fillText("Face OK", x + 8, Math.max(0, y - 24) + 16);
          }
        }
      } catch { /* skip this frame */ }
      if (!stop) timer = setTimeout(tick, 250);
    };
    tick();
    return () => { stop = true; clearTimeout(timer); };
  }, [faceState, camOn]);

  const capture = () => {
    const v = videoRef.current;
    if (!v || !streamRef.current || !v.videoWidth) return "";
    const c = document.createElement("canvas");
    c.width = 480;
    c.height = Math.round((v.videoHeight / v.videoWidth) * 480);
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    shotCanvasRef.current = c;
    const url = c.toDataURL("image/jpeg", 0.75);
    setPhoto(url);
    return url;
  };

  const print = (ticket) => printHtml(renderToStaticMarkup(<AnnaTicketSlip ticket={ticket} temple={temple} />), "receipt-page", `Annaprasadam_${ticket.ticket_no}`);

  const issue = async (cat) => {
    if (busyRef.current) return;
    const shot = capture();
    if (!shot) { toast.error("Camera not ready — a photo is needed to issue the ticket."); return; }
    busyRef.current = true;
    setBusy(true);
    try {
      let face;
      if (faceDays) {
        if (faceState === "error") throw new Error("Face recognition could not load on this PC. Check the internet connection and reload the page.");
        face = await faceTemplate(shotCanvasRef.current);
        if (!face) throw new Error("No face detected. Ask the devotee to look at the camera, then press the key again.");
      }
      const r = await api.post("/anna/tickets", { ticket_type: type, category: cat, photo: shot, temple_id: templeId, face });
      setBlocked(null);
      setLast(r.data);
      print(r.data);
      setStats((s) => s && { ...s, [cat]: (s[cat] || 0) + 1, total: (s.total || 0) + 1 });
      toast.success(`Ticket ${r.data.ticket_no} · ${r.data.category_name}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d && typeof d === "object") {
        setBlocked({ ...d, photo: shot });
        toast.error(d.message, { duration: 8000 });
        try { navigator.vibrate?.([250, 120, 250]); } catch { /* ignore */ }
      } else {
        toast.error(fmtErr(e));
      }
    } finally {
      setTimeout(() => { busyRef.current = false; setBusy(false); }, 800); // a double press makes one ticket
    }
  };

  // Keyboard: M / F / C / O issue + print, Space captures the photo.
  const issueRef = useRef(issue);
  issueRef.current = issue;
  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey || e.altKey || e.metaKey || e.repeat) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.closest?.("[role=dialog],[role=listbox]"))) return;
      if (e.key === " ") { e.preventDefault(); capture(); return; }
      const k = (e.key || "").toUpperCase();
      if (ANNA_CATS.some((c) => c.key === k)) { e.preventDefault(); issueRef.current(k); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []); // eslint-disable-line

  return (
    <div className="max-w-5xl mx-auto" data-testid="anna-issue-page">
      <div className="text-center mb-4">
        <p className="text-xs uppercase tracking-[0.25em] text-gold-700 font-semibold">Annaprasadam Ticket Issue &amp; Verification System</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-maroon-900 flex items-center justify-center gap-2">
          <Soup className="w-7 h-7 text-gold-600" /> {ANNA_TYPES.find((x) => x.key === type)?.label} Ticket
        </h1>
        {temple && <p className="text-sm text-slate-600">{temple.name}{counterName ? ` · ${counterName}` : ""}</p>}
      </div>

      <DirectPrintBanner templeId={templeId} />

      {faceDays > 0 && (
        <p className={`mb-3 mx-auto w-fit rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1.5 ${faceState === "ready" ? "bg-green-50 text-green-800 border border-green-200" : faceState === "error" ? "bg-red-50 text-red-700 border border-red-200" : "bg-amber-50 text-amber-800 border border-amber-200"}`} data-testid="anna-face-status">
          <ScanFace className="w-4 h-4" /> Face check ON · one token per devotee every {faceDays} days ·{" "}
          {faceState === "ready" ? "ready" : faceState === "error" ? "could not load - reload the page" : "loading…"}
        </p>
      )}

      <div className="flex flex-wrap justify-center gap-2 mb-4">
        {user.role === "SUPER_ADMIN" && temples.length > 1 && (
          <select value={templeId} onChange={(e) => setTempleId(e.target.value)} className="h-10 rounded-lg border border-[#E5D9C5] px-3 text-sm bg-white" data-testid="anna-temple">
            {temples.map((t) => <option key={t.temple_id} value={t.temple_id}>{t.temple_id} · {t.name}</option>)}
          </select>
        )}
        {ANNA_TYPES.map((x) => (
          <button key={x.key} type="button" onClick={() => setType(x.key)} data-testid={`anna-type-${x.key}`}
            className={`h-10 px-5 rounded-xl font-semibold text-sm shadow-sm bg-gradient-to-br ${x.cls} ${type === x.key ? "ring-2 ring-maroon-700 text-maroon-900" : "opacity-70 text-slate-800"}`}>
            {x.label}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="card-gold-top">
          <CardContent className="p-4">
            <p className="font-semibold text-maroon-900 text-center mb-2">Live Camera</p>
            <div className="rounded-xl overflow-hidden bg-slate-900 aspect-[4/3] flex items-center justify-center">
              <div className={`relative w-full h-full ${camOn ? "" : "hidden"}`}>
                <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="anna-video" />
                <canvas ref={overlayRef} className="absolute inset-0 w-full h-full pointer-events-none" data-testid="anna-face-overlay" />
                {faceState === "ready" && faceSeen === false && (
                  <p className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-red-600/90 px-3 py-1 text-xs font-semibold text-white" data-testid="anna-no-face">
                    No face detected - ask the devotee to look at the camera
                  </p>
                )}
              </div>
              {!camOn && <p className="text-slate-300 text-sm px-4 text-center">{camError || "Camera is off"}</p>}
            </div>
            <div className="flex justify-center gap-2 mt-3">
              <Button type="button" onClick={capture} disabled={!camOn} className="bg-sky-700 hover:bg-sky-800 text-white" data-testid="anna-capture"><Camera className="w-4 h-4 mr-1" /> Capture</Button>
              {camOn
                ? <Button type="button" variant="outline" onClick={stopCamera}><CameraOff className="w-4 h-4 mr-1" /> Stop</Button>
                : <Button type="button" variant="outline" onClick={startCamera}><Camera className="w-4 h-4 mr-1" /> Start</Button>}
            </div>
          </CardContent>
        </Card>

        <Card className="card-gold-top">
          <CardContent className="p-4 flex flex-col">
            <p className="font-semibold text-maroon-900 text-center mb-2">Issue Ticket</p>
            <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 aspect-[4/3] flex items-center justify-center overflow-hidden">
              {photo ? <img src={photo} alt="captured" className="w-full h-full object-contain" data-testid="anna-photo" />
                : <p className="text-slate-400 text-sm">No photo captured yet</p>}
            </div>
            <div className="grid grid-cols-4 gap-2 mt-3">
              {ANNA_CATS.map((c) => (
                <button key={c.key} type="button" disabled={busy || !templeId} onClick={() => issue(c.key)} data-testid={`anna-issue-${c.key}`}
                  title={`${c.label} (key ${c.key})`}
                  className="h-16 rounded-2xl bg-gradient-to-b from-yellow-300 to-amber-400 hover:from-yellow-400 hover:to-amber-500 shadow text-maroon-900 disabled:opacity-60 transition">
                  <span className="block text-2xl font-black leading-none">{c.key}</span>
                  <span className="block text-[11px] font-semibold mt-0.5">{c.label}</span>
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500 text-center mt-2 flex items-center justify-center gap-1">
              <Keyboard className="w-4 h-4" /> Press <b>M</b> / <b>F</b> / <b>C</b> / <b>O</b> to issue &amp; print · <b>Space</b> = capture
            </p>
            {onWindowsPc && directPrintOn() && (
              <p className="text-[11px] text-green-700 font-semibold text-center mt-1 flex items-center justify-center gap-1" data-testid="anna-direct-print-on">
                <CheckCircle2 className="w-3.5 h-3.5" /> Direct printing ON — tickets print without the print dialog
              </p>
            )}
            {blocked && (
              <div className="mt-3 rounded-lg border-2 border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 flex gap-3 items-start" data-testid="anna-face-blocked">
                {blocked.photo && <img src={blocked.photo} alt="" className="w-14 h-14 object-cover rounded border border-red-200" />}
                <div className="flex-1">
                  <p className="font-bold flex items-center gap-1"><Ban className="w-4 h-4" /> Token not issued</p>
                  <p>{blocked.message}</p>
                  <p className="text-xs mt-0.5">Earlier token <b className="font-mono">{blocked.ticket_no}</b> · {istDateTime(blocked.issued_at)} · next token from {istDateTime(blocked.next_allowed)}</p>
                </div>
              </div>
            )}
            {last && (
              <div className="mt-3 rounded-lg bg-gold-50 border border-gold-300 px-3 py-2 text-sm flex items-center justify-between gap-2" data-testid="anna-last">
                <span><span className="text-slate-500">Last:</span> <b className="font-mono">{last.ticket_no}</b> · {last.category_name} · {istDateTime(last.created_at)}</span>
                <Button type="button" size="sm" variant="outline" onClick={() => print(last)}><Printer className="w-4 h-4 mr-1" /> Reprint</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-4">
        <CardContent className="p-4">
          <p className="font-heading font-bold text-maroon-900 text-center mb-3">Live Statistics (Today · {ANNA_TYPES.find((x) => x.key === type)?.label})</p>
          <AnnaStats stats={stats} />
        </CardContent>
      </Card>
    </div>
  );
}
